/**
 * ContratsPortefeuille — Répartition portefeuille par Type/Produit/Vendeur/Revendeur.
 * Port BE-TAYE — Pie + Bar chart + tableau détaillé.
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  PieChart, Pie, Cell, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { Briefcase, Users, Building2, Handshake } from "lucide-react";
import { toast } from "sonner";

const COLORS = ["#3b82f6", "#06b6d4", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6"];
const fmtEUR = (v) => new Intl.NumberFormat("fr-FR",
  { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(v || 0);

export default function ContratsPortefeuille() {
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("type");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/contracts/stats/portfolio");
        setPortfolio(r.data);
      } catch (e) { toast.error(apiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const viewOptions = {
    type: { key: "by_type", title: "Par Type de Contrat", icon: Briefcase },
    produit: { key: "by_produit", title: "Par Produit", icon: Building2 },
    vendeur: { key: "by_vendeur", title: "Par Vendeur", icon: Users },
    revendeur: { key: "by_revendeur", title: "Par Revendeur", icon: Handshake },
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-slate-100 animate-pulse rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="h-80 bg-slate-100 animate-pulse rounded-lg" />
          <div className="h-80 bg-slate-100 animate-pulse rounded-lg" />
        </div>
      </div>
    );
  }

  const currentData = portfolio?.[viewOptions[view].key] || [];
  const pieData = currentData.map((item, i) => ({ ...item, color: COLORS[i % COLORS.length] }));
  const totalMRR = currentData.reduce((s, i) => s + (i.mrr || 0), 0);

  return (
    <div className="space-y-6" data-testid="contrats-portefeuille-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            Portefeuille Contrats
          </h1>
          <p className="text-slate-500 text-sm">Répartition et analyse du portefeuille</p>
        </div>
        <Select value={view} onValueChange={setView}>
          <SelectTrigger className="w-48" data-testid="portfolio-view-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="type">Par Type</SelectItem>
            <SelectItem value="produit">Par Produit</SelectItem>
            <SelectItem value="vendeur">Par Vendeur</SelectItem>
            <SelectItem value="revendeur">Par Revendeur</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              {(() => {
                const Icon = viewOptions[view].icon;
                return <Icon className="w-4 h-4 text-blue-600" />;
              })()}
              {viewOptions[view].title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <div className="h-80 min-w-0">
                <ResponsiveContainer width="99%" height="100%" minWidth={0}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={100}
                      dataKey="mrr" label={({ percent }) => `${(percent * 100).toFixed(0)}%`}>
                      {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip formatter={(v) => [fmtEUR(v), "MRR"]} contentStyle={{ borderRadius: "8px" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-80 flex items-center justify-center text-slate-500">
                Aucune donnée disponible
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">MRR {viewOptions[view].title}</CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <div className="h-80 min-w-0">
                <ResponsiveContainer width="99%" height="100%" minWidth={0}>
                  <BarChart data={pieData.slice(0, 8)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k€`} />
                    <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => fmtEUR(v)} />
                    <Bar dataKey="mrr" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-80 flex items-center justify-center text-slate-500">
                Aucune donnée disponible
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Détail {viewOptions[view].title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="text-left py-3 px-4 font-medium text-slate-500">Nom</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-500">Contrats</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-500">MRR</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-500">% du MRR</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-500">CA Leaser</th>
                </tr>
              </thead>
              <tbody>
                {pieData.map((item, i) => (
                  <tr key={i} className="border-b hover:bg-slate-50"
                       data-testid={`portfolio-row-${i}`}>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="font-medium text-slate-900">{item.name}</span>
                      </div>
                    </td>
                    <td className="text-right py-3 px-4 font-mono">{item.count}</td>
                    <td className="text-right py-3 px-4 font-mono font-bold">{fmtEUR(item.mrr)}</td>
                    <td className="text-right py-3 px-4 font-mono">
                      {totalMRR > 0 ? ((item.mrr / totalMRR) * 100).toFixed(1) : 0}%
                    </td>
                    <td className="text-right py-3 px-4 font-mono">{fmtEUR(item.ca_leaser)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 font-bold">
                  <td className="py-3 px-4">Total</td>
                  <td className="text-right py-3 px-4 font-mono">{pieData.reduce((s, i) => s + i.count, 0)}</td>
                  <td className="text-right py-3 px-4 font-mono">{fmtEUR(totalMRR)}</td>
                  <td className="text-right py-3 px-4 font-mono">100%</td>
                  <td className="text-right py-3 px-4 font-mono">{fmtEUR(pieData.reduce((s, i) => s + i.ca_leaser, 0))}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
